<div [hidden]="isInitialLoading">
    @if (detail) {
        <a [routerLink]="['/field-tools/swppp/site', detail.site.id]">{{ detail.site.name }}</a>
        <h1 class="h4">Vancon Inc SWPPP Construction Inspection Form</h1>
        <div class="sticky-top bg-white py-2 border-bottom mb-3">
            @if (canEdit && !signed) {
                <button type="button" class="btn btn-sm btn-outline-primary" (click)="focusAction()">Add Action Item</button>
                <label class="btn btn-sm btn-outline-secondary mb-0">
                    Photos
                    <input type="file" accept="image/*" multiple hidden (change)="onPhotos($event)" />
                </label>
                <button type="button" class="btn btn-sm btn-link" (click)="showDisclaimer = !showDisclaimer">Disclaimer</button>
            }
        </div>
        @if (showDisclaimer) {
            <div class="alert alert-secondary">{{ detail.disclaimerText }}</div>
        }
        <label class="form-label">Inspection date and time</label>
        <input class="form-control mb-3" type="datetime-local" [ngModel]="localDateTime()" (ngModelChange)="setDateTime($event)" [disabled]="signed" />

        <h5>Permits</h5>
        @for (permit of detail.permits; track permit.id) {
            <div class="border rounded p-2 mb-2">
                <div>Permit number {{ permit.permitNumber }}</div>
                <div>{{ permit.permitType }}</div>
                <div>Effective {{ permit.effectiveDate }} · Expires {{ permit.expirationDate }}</div>
                <div>{{ permit.permitDescription }}</div>
            </div>
        }

        <h5>Attached site maps</h5>
        <div class="d-flex flex-wrap gap-2 align-items-start mb-3">
            <select class="form-select" style="max-width: 28rem" [(ngModel)]="detail.inspection.siteMapDocumentId" [disabled]="signed">
                <option [ngValue]="null">Select a map</option>
                @for (map of detail.siteMaps; track map.id) {
                    <option [ngValue]="map.id">{{ map.title }}</option>
                }
            </select>
            @if (canEdit && !signed) {
                <label class="btn btn-outline-secondary mb-0">
                    Add site map
                    <input type="file" accept="image/*,.pdf" hidden (change)="onSiteMap($event)" />
                </label>
            }
        </div>

        <h5>Current open action items</h5>
        @for (item of detail.openActionItems; track item.id) {
            <div class="border rounded p-2 mb-2">{{ item.description }}</div>
        }
        @if (canEdit && !signed) {
            <textarea #actionBox class="form-control mb-2" rows="2" placeholder="New action item" [(ngModel)]="actionText"></textarea>
            <button type="button" class="btn btn-sm btn-outline-primary mb-3" (click)="addAction()">Add action item</button>
        }

        @if (detail.lastInspectionId && canEdit && !signed) {
            <div class="mb-3">
                <div>Copy and edit last inspection</div>
                <div class="small text-muted">Copying does not include pictures, attachments, or action items. Performed by {{ detail.lastInspectionBy }} {{ detail.lastInspectionOn | date: 'MMM d, y' }}</div>
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="copyLast()">Copy and edit last inspection</button>
            </div>
        }

        @if (detail.recentPrecip.length) {
            <h5>Weather history</h5>
            <ul>
                @for (day of detail.recentPrecip; track day.id) {
                    <li>{{ day.date }} · {{ day.precipInches }}"</li>
                }
            </ul>
        }

        @for (group of groups; track group.key) {
            <div class="card mb-3">
                <div class="card-header">{{ group.title }}</div>
                <div class="card-body">
                    @for (question of group.questions; track question.id) {
                        <div class="mb-3">
                            <div>{{ question.text }}</div>
                            @if (question.answerType === 'Text' || question.answerType === 'Number' || question.answerType === 'Date') {
                                <input class="form-control" [type]="question.answerType === 'Date' ? 'date' : question.answerType === 'Number' ? 'number' : 'text'" [(ngModel)]="answer(question).value" [disabled]="signed" />
                            }
                            @if (question.answerType === 'MultiChoice') {
                                @for (option of options(question); track option) {
                                    <label class="form-check">
                                        <input class="form-check-input" type="checkbox" [checked]="chosen(question, option)" (change)="toggleChoice(question, option)" [disabled]="signed" />
                                        {{ option }}
                                    </label>
                                }
                                @if (question.allowOtherText) {
                                    <input class="form-control" placeholder="Other" [(ngModel)]="answer(question).otherText" [disabled]="signed" />
                                }
                            }
                            @if (question.answerType === 'YesNoNA') {
                                @for (option of ['Yes', 'No', 'NA']; track option) {
                                    <label class="form-check form-check-inline">
                                        <input class="form-check-input" type="radio" [name]="question.id" [value]="option" [(ngModel)]="answer(question).value" [disabled]="signed" />
                                        {{ option }}
                                    </label>
                                }
                                @if (isProblem(question) && canEdit && !signed) {
                                    <button type="button" class="btn btn-sm btn-outline-warning" (click)="addProblemAction(question)">Add action item</button>
                                }
                            }
                        </div>
                    }
                </div>
            </div>
        }

        <label class="form-label">Comments</label>
        <textarea class="form-control mb-3" rows="4" [(ngModel)]="detail.inspection.comments" [disabled]="signed"></textarea>

        @if (canEdit && !signed) {
            <h5>Add visual inspection</h5>
            <input id="visualFile" type="file" accept="image/*" class="form-control mb-1" />
            <input class="form-control mb-2" placeholder="Location note" [(ngModel)]="visualNote" />
            <button type="button" class="btn btn-sm btn-outline-secondary mb-3" (click)="saveVisual()">Add photo</button>
        }

        <h5>Photos</h5>
        <div class="d-flex flex-wrap gap-2 mb-3">
            @for (image of detail.images; track image.id) {
                <figure>
                    <img [src]="imageUrl(image)" [alt]="image.fileName" style="max-width: 180px" />
                    <figcaption class="small">{{ image.caption || image.fileName }}</figcaption>
                </figure>
            }
        </div>

        <h5>Electronic signature</h5>
        <div>X {{ signed ? detail.inspection.signedName : signedName }}</div>
        @if (!signed) {
            <input class="form-control mb-2" placeholder="Name" [(ngModel)]="signedName" />
            <input class="form-control mb-2" placeholder="Title" [(ngModel)]="signerTitle" />
            <label class="form-check"><input class="form-check-input" type="checkbox" [(ngModel)]="confirmed" /> I confirm that I signed above.</label>
        } @else {
            <div>{{ detail.inspection.signerTitle }} · {{ detail.inspection.signedOn | date: 'MMM d, y h:mm a' }}</div>
        }

        @if (message) {
            <div class="alert alert-success mt-2">{{ message }}</div>
        }

        @if (canEdit && !signed) {
            <div class="mt-3">On signing, this will be emailed to: {{ recipientNames || 'no one on this site has an email address' }}</div>
            <div class="mt-3 d-flex flex-wrap gap-2">
                <button type="button" class="btn btn-primary" [disabled]="!confirmed" (click)="sign()">Sign</button>
                <button type="button" class="btn btn-outline-secondary" (click)="saveDraft()">Finish Later</button>
                <button type="button" class="btn btn-outline-danger" (click)="deleteInspection()">Delete Inspection</button>
            </div>
        }
    }
</div>
